import { GeoPoint, distanceKm } from './geo'

export interface ElevationSample {
  /** Jarak dari titik A dalam km. */
  distance: number
  /** Ketinggian dataran (meter di atas permukaan laut). */
  elevation: number
  lat: number
  lng: number
}

export interface TerrainProfile {
  samples: ElevationSample[]
  totalDistance: number
  maxElevation: ElevationSample
  minElevation: ElevationSample
  /** Titik tertinggi relatif terhadap garis lurus A → B (meter). */
  worstClearance: number
  obstructed: boolean
}

const SAMPLE_COUNT = 60

/** Interpolasi linear antar dua koordinat — cukup akurat untuk jarak link < 200 km. */
function interpolate(a: GeoPoint, b: GeoPoint, t: number): GeoPoint {
  return {
    lat: a.lat + (b.lat - a.lat) * t,
    lng: a.lng + (b.lng - a.lng) * t,
  }
}

/**
 * Mengambil profil ketinggian dataran antara dua site melalui
 * Open-Meteo Elevation API (dataset Copernicus DEM 90 m, tanpa API key).
 */
export async function fetchTerrainProfile(
  from: GeoPoint,
  to: GeoPoint,
): Promise<TerrainProfile> {
  const points: GeoPoint[] = Array.from({ length: SAMPLE_COUNT }, (_, i) =>
    interpolate(from, to, i / (SAMPLE_COUNT - 1)),
  )

  const url =
    'https://api.open-meteo.com/v1/elevation?latitude=' +
    points.map((p) => p.lat.toFixed(5)).join(',') +
    '&longitude=' +
    points.map((p) => p.lng.toFixed(5)).join(',')

  const response = await fetch(url)
  if (!response.ok) throw new Error('Gagal memuat data ketinggian dataran.')

  const payload = (await response.json()) as { elevation?: number[] }
  const elevations = payload.elevation
  if (!elevations?.length) throw new Error('Data ketinggian tidak tersedia.')

  const totalDistance = distanceKm(from, to)

  const samples: ElevationSample[] = points.map((point, i) => ({
    distance: (totalDistance * i) / (SAMPLE_COUNT - 1),
    elevation: elevations[i] ?? 0,
    lat: point.lat,
    lng: point.lng,
  }))

  const maxElevation = samples.reduce((best, s) =>
    s.elevation > best.elevation ? s : best,
  )
  const minElevation = samples.reduce((best, s) =>
    s.elevation < best.elevation ? s : best,
  )

  // Garis pandang lurus dari ketinggian tanah A ke ketinggian tanah B.
  const startElevation = samples[0].elevation
  const endElevation = samples[samples.length - 1].elevation

  const worstClearance = samples.reduce((worst, s, i) => {
    const t = i / (SAMPLE_COUNT - 1)
    const lineOfSight = startElevation + (endElevation - startElevation) * t
    return Math.max(worst, s.elevation - lineOfSight)
  }, -Infinity)

  return {
    samples,
    totalDistance,
    maxElevation,
    minElevation,
    worstClearance,
    obstructed: worstClearance > 0,
  }
}
